/**
 * CSV Import: small upload + column mapping + commit flow used inside
 * the Booking Intake Hub. We deliberately keep the UI single-screen so
 * staff doesn't lose context: upload, see preview + suggested mapping,
 * tweak, hit Import.
 *
 * Imports go through the normal draft flow: nothing is auto-confirmed.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, ArrowRight, Loader2, ExternalLink, Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";

const CANONICAL_LABELS = {
  customer_name: "Customer name",
  customer_email: "Customer email",
  customer_phone: "Customer phone",
  vehicle_text: "Vehicle (text: we'll match it)",
  start_datetime: "Pickup date/time",
  end_datetime: "Return date/time",
  pickup_location: "Pickup location",
  return_location: "Return location",
  price: "Price (PHP)",
  deposit: "Deposit (PHP)",
  customer_notes: "Customer-visible notes",
  internal_notes: "Internal notes",
};

export default function CsvImportPanel({ onCommitted }) {
  const [file, setFile] = useState(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/booking-intake/csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSheet = async () => {
    if (!sheetUrl.trim()) { toast.error("Paste your sheet URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booking-intake/google-sheet/preview",
                                       { url: sheetUrl });
      setPreview(data);
      setMapping(data.mapping || {});
      setFile({ name: "Google Sheet" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview?.rows?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/booking-intake/csv/commit", {
        rows: preview.rows, mapping,
        source_channel: file?.name === "Google Sheet" ? "google_sheet" : "csv",
      });
      toast.success(
        `Imported ${data.imported} draft${data.imported === 1 ? "" : "s"}` +
        (data.duplicates?.length
          ? ` · ${data.duplicates.length} possible duplicate${data.duplicates.length === 1 ? "" : "s"} flagged`
          : "") +
        (data.failed?.length ? ` · ${data.failed.length} row${data.failed.length === 1 ? "" : "s"} failed` : "")
      );
      setPreview(null); setFile(null); setSheetUrl(""); setMapping({});
      onCommitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const exportUrl = `${API_BASE}/booking-intake/csv/export`;

  return (
    <div className="space-y-5" data-testid="csv-import-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-lg font-semibold">Import from CSV or Google Sheet</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Upload a CSV or paste a publicly-shared Google Sheet URL. We'll
              preview the rows and let you map each column to a ConfirmPH
              field. Every row becomes a reviewable draft, never an
              auto-confirmed booking.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="rounded-lg border border-dashed border-slate-300 p-4 flex flex-col items-center justify-center text-center text-sm cursor-pointer hover:border-emerald-500 transition"
                  data-testid="csv-upload-label">
            <Upload className="w-5 h-5 text-emerald-700 mb-1" />
            {file?.name && file.name !== "Google Sheet"
              ? <span className="font-medium">{file.name}</span>
              : <>
                  <span className="font-medium">Upload CSV file</span>
                  <span className="text-xs text-slate-500">Max 2 MB · UTF-8</span>
                </>}
            <input type="file" accept=".csv,text/csv" className="hidden"
                    data-testid="csv-file-input"
                    onChange={handleFile} />
          </label>

          <div className="rounded-lg border border-dashed border-slate-300 p-4 flex flex-col gap-2 text-sm">
            <span className="font-medium inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4 text-emerald-700" /> Google Sheets
            </span>
            <input
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              data-testid="sheet-url-input"
              placeholder="Paste a public sheet URL"
              className="h-9 rounded border border-slate-300 px-2 text-sm"
            />
            <button
              type="button"
              onClick={handleSheet}
              disabled={busy}
              data-testid="sheet-fetch-btn"
              className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <ArrowRight className="w-3.5 h-3.5" />}
              Fetch sheet
            </button>
            <p className="text-[11px] text-slate-500">
              Use <em>File → Share → Anyone with the link · Viewer</em>, or
              <em> File → Share → Publish to web</em>. We don't read private
              sheets: OAuth coming in a later release.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-slate-500">
            Need to share bookings back into Sheets? Export the latest as CSV.
          </p>
          <a
            href={exportUrl}
            data-testid="csv-export-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1B4332] hover:underline"
          >
            <Download className="w-4 h-4" /> Export bookings CSV
          </a>
        </div>
      </div>

      {preview && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4"
              data-testid="csv-preview-panel">
          <header className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-display text-base font-semibold">
                Preview · {preview.total_rows} row{preview.total_rows === 1 ? "" : "s"}
              </h3>
              <p className="text-xs text-slate-500">
                Tweak any column that's wrong, then click Import to create drafts.
                You'll review every row before the customer sees anything.
              </p>
            </div>
            <button
              type="button"
              onClick={commit}
              disabled={busy}
              data-testid="csv-commit-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg btn-brand font-medium text-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Import {preview.total_rows} row{preview.total_rows === 1 ? "" : "s"} as drafts
            </button>
          </header>

          <div className="overflow-x-auto -mx-2">
            <table className="text-xs w-full">
              <thead>
                <tr className="bg-slate-50">
                  {preview.headers.map((h) => (
                    <th key={h} className="text-left p-2 align-top">
                      <div className="font-semibold text-slate-700">{h}</div>
                      <select
                        value={mapping[h] || ""}
                        onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}
                        data-testid={`csv-map-${h}`}
                        className="mt-1 h-8 rounded border border-slate-300 bg-white px-1 text-xs w-full"
                      >
                        <option value="">- ignore -</option>
                        {Object.entries(CANONICAL_LABELS).map(([k, label]) => (
                          <option key={k} value={k}>{label}</option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview_rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {preview.headers.map((h) => (
                      <td key={h} className="p-2 align-top text-slate-700">
                        {String(r[h] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/app/booking-intake"
                 className="text-xs text-slate-500 hover:text-emerald-700">
            ← Back to Quick Note / Paste Chat
          </Link>
        </div>
      )}
    </div>
  );
}
